import React from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Centered from './centered';
import Empty from './empty';
import { isWebsitePath } from '../util/marketingRoutes';
// The authenticated dashboard is never used on public website routes. Load
// its layout only when a dashboard route renders, so its dependencies stay out
// of the marketing entry bundle.
const Layout1 = dynamic(() => import('./layout-1'), { ssr: false });

const Layouts = ({ children }) => {
  const router = useRouter();

  if (router.isFallback) {
    <h1>Data is loading</h1>;
  }
  let { pathname } = { ...router };
  // New native marketing routes inherit the public shell automatically.
  if (isWebsitePath(pathname)) return <Empty>{children}</Empty>;
  // Error pages render their own full-bleed branded shell, so they take Empty
  // rather than Centered. Do NOT move them back: Centered is shared with the
  // auth routes below (/login, /create-account, /magic-login, /signup/free…),
  // so its gray-50/gray-900 background cannot be rebranded for the error pages
  // without restyling every one of those too.
  if (['/404', '/500'].includes(pathname)) {
    return <Empty>{children}</Empty>;
  }
  if (
    [
      '/[slug]/login',
      '/[slug]/create-account',
      '/[slug]/forgot-password',
      '/[slug]/reset-password',
      '/[slug]/onboarding',
      '/[slug]/confirmation',
      '/[slug]/redirect',
      '/[slug]/error',
      '/login',
      '/contact-us-1',
      '/create-account',
      // Free-tier signup is public + unauthenticated, same shape as
      // /create-account. Without this entry it falls through to Layout1,
      // which gates on a company in cookies and hangs on "Loading company
      // data..." for visitors who don't have one yet.
      '/signup/free',
      '/email-confirmation',
      '/logout',
      '/reset-password',
      '/forgot-password',
      '/lock-screen',
      '/subscribe',
      '/error-page',
      '/coming-soon',
      '/redirect',
      '/session-conflict',
      // Signup-closed landing (pages/request-access.js): may be reached signed
      // in with no company, so it must bypass withAuth like the pages above.
      '/request-access',
      // OAuth return + magic-link verify: unauthenticated, transient pages that
      // establish the session themselves. Must be Centered, NOT Layout1 — else
      // withAuth gates them and hangs on "Loading company data..." (same trap
      // as /signup/free above).
      '/connect/[provider]/redirect',
      '/magic-login',
      // Free-tier store-setup for OAuth/magic signups: the user is authenticated
      // but has NO company yet, so it must bypass withAuth (Layout1) exactly like
      // the transient auth pages above — otherwise it hangs on "Loading company
      // data...". Membership is EXACT-match, so '/onboarding' (in the Empty
      // allowlist below) does NOT cover this child path.
      '/onboarding/free',
    ].includes(pathname)
  ) {
    return <Centered>{children}</Centered>;
  } else if (
    [
      '/',
      '/blog',
      // Membership here is EXACT-match on the ROUTE PATTERN, not the URL, so a
      // new blog route needs its own entry — /blog does not cover
      // /blog/page/[page]. Miss it and the page falls through to the dashboard
      // layout, which renders nothing for a logged-out visitor: the route still
      // returns 200 with correct <Head> tags and full __NEXT_DATA__, but an
      // empty body. Invisible to a unit test that renders the page component
      // directly; only a real request through _app + this dispatcher shows it.
      '/blog/[slug]',
      '/blog/page/[page]',
      '/beta',
      // Marketing site — sitemap routes per TIMBERCLOUD_SITE_BUILD_PLAN
      '/about',
      '/help',
      '/product',
      '/product/commerce',
      '/product/production',
      '/product/pricing-engine',
      '/product/ai',
      '/product/mobile',
      '/product/integrations',
      '/solutions',
      '/solutions/cabinet-makers',
      '/solutions/door-manufacturers',
      '/solutions/millwork-and-trim',
      '/solutions/custom-furniture',
      // Native industry pages (this rebuild). Membership here is EXACT-match
      // on the route pattern, so each new native /solutions/* file needs its
      // own entry — otherwise it falls through to Layout1 (the dashboard)
      // and renders an empty body for logged-out visitors.
      '/solutions/production-cabinet-manufacturers',
      '/customers',
      '/customers/portadoor',
      '/customers/ds-millwork',
      '/compare',
      '/compare/allmoxy',
      '/compare/innergy',
      '/compare/patchwork-stack',
      '/demo',
      '/trust',
      '/careers',
      '/press',
      '/legal/privacy',
      '/legal/terms',
      '/legal/cookies',
      '/landing',
      '/onboarding',
      '/onboarding/activate',
      '/account',
      '/login-1',
      '/login-2',
      '/login-3',
      '/accounts',
      // The company-free QuickBooks link Intuit points at (pages/quickbooks.js):
      // no company is chosen yet, so no dashboard shell.
      '/quickbooks',
      '/user',
      // Dev-only stage scripts/render-template-stills.mjs screenshots headless,
      // with no session: Layout1 would gate it on a company and never draw it.
      '/_dev-template-still',
    ].includes(pathname)
  ) {
    return <Empty>{children}</Empty>;
  } else {
    return <Layout1>{children}</Layout1>;
  }
};

export default Layouts;
